import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { debounceTime, distinctUntilChanged, switchMap } from "rxjs/operators";
import { Subject, EMPTY } from "rxjs";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { QuotesLineAddressDto } from "../../../../core/models/qoutes/quotesLineAddressDto";
import { GetAllVendorsDto } from "../../../../core/models/vendor/getAllVendors";
import { SamplepoListingService } from "../../../../core/services/samplepo-listing.service";
import { CommonModule } from "@angular/common";
import { SelectModule } from "primeng/select";
import { TableModule } from "primeng/table";
import { VendorSummaryComponent } from "../../../vendors/components/vendor-summary/vendor-summary.component";
import { ModalStackerComponent } from "../../../../shared/components/modal-stacker/modal-stacker.component";
import { Popover, PopoverModule } from "primeng/popover";
import { ScrollerModule } from "primeng/scroller";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";

@UntilDestroy()
@Component({
  selector: "jni-line-vendor",
  templateUrl: "./line-vendor.component.html",
  styleUrls: ["./line-vendor.component.scss"],
  imports: [
    CommonModule,
    SelectModule,
    ReactiveFormsModule,
    TableModule,
    VendorSummaryComponent,
    ModalStackerComponent,
    PopoverModule,
    ScrollerModule,
    ButtonModule,
    CardModule,
    InputTextModule,
  ],
})
export class LineVendorComponent implements OnInit {
  showSmallLoader = false;
  vendorList: GetAllVendorsDto[] = [];
  @Input() vendorAccountNumber = "";
  vendorDisplayModal = false;

  @Input() poAssignment = "New";
  @Input() formErrors!: Record<string, string>;
  @Input() quoteOrderLine: QuotesLineAddressDto[] = [];
  poAssigment = [
    {
      id: 0,
      name: "Default",
      status: false,
    },
    {
      id: 1,
      name: "New",
      status: false,
    },
    {
      id: 2,
      name: "Existing",
      status: false,
    },
  ];
  public searchVendor$ = new Subject<string>();
  @Input() parent!: FormGroup;
  @Output() vendorAccountEvent = new EventEmitter();
  @Output() vendorObjectEvent = new EventEmitter();
  @Output() validationErrorEvent = new EventEmitter();

  constructor(private _samplepoListingService: SamplepoListingService) {}

  ngOnInit(): void {
    this.searchVendorName();
  }

  searchVendorName() {
    this.searchVendor$
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap((searchitem: string) => {
          this.showSmallLoader = true;
          const accountNumberOrName = searchitem;
          this.vendorList = [];
          if (accountNumberOrName != "" && accountNumberOrName != undefined) {
            return this._samplepoListingService.getVendorByNameAndAccountNumberFilter(
              accountNumberOrName
            );
          }
          return EMPTY;
        })
      )
      .subscribe({
        next: res => {
          if (res.data != null) {
            this.vendorList = res.data as unknown as GetAllVendorsDto[];
          }
          this.showSmallLoader = false;
        },
        error: () => {
          this.showSmallLoader = false;
        },
      });
  }

  poAssignmentStatus() {
    this.poAssigment[2].status = true;
  }

  onVendorSearchOpenToggle(
    mydrop: Popover,
    event: KeyboardEvent,
    target: HTMLElement
  ) {
    const value = event.target as HTMLInputElement;
    if (value.value === "") {
      this.parent.patchValue({
        vendor: {
          duplicateVendorDirectDelivery: "",
          vendorDirectDelivery: "",
          vendorPoAssignment: "",
          vendorName: "",
          vendorAddress: "",
          vendorPhone: "",
          vendorEmail: "",
          vendorFax: "",
        },
      });
    }
    mydrop.show(event, target);
    this.vendorAccountEvent.emit(event);
  }

  onVendorRowSelection(
    vendorSelectedRow: GetAllVendorsDto,
    vendorToggle: Popover
  ) {
    this.showSmallLoader = true;
    this.clearVendorForm();
    const partyNumber = vendorSelectedRow.partyNumber;
    const accountNumber = vendorSelectedRow.vendorAccountNumber;
    this.patchPoAssignment(accountNumber ?? "");
    this.vendorObjectEvent.emit(vendorSelectedRow);
    this._samplepoListingService
      .getVendorAddressByPartyNumber(partyNumber, accountNumber)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.showSmallLoader = false;
          if (res.data) {
            const primaryAddress = res.data.filter(
              x =>
                x.isPrimaryD365?.toLowerCase() === "yes" ||
                x.isPrimaryD365?.toLowerCase() === "1"
            );
            if (primaryAddress.length > 0) {
              if (primaryAddress[0].addressD365) {
                this.patchValuesInVendorFormAddressField(
                  primaryAddress[0].addressD365
                );
              }
            }
          }
        },
        error: () => {
          this.showSmallLoader = false;
        },
      });
    this.patchValuesInVendorForm(vendorSelectedRow);
    this.patchAccountNumber(accountNumber ?? "");
    vendorToggle.hide();
  }

  clearVendorForm() {
    this.parent.patchValue({
      header: {
        vendorName: "",
        vendorPhone: "",
        vendorEmail: "",
        vendorAddress: "",
        vendorFax: "",
      },
    });
  }

  patchValuesInVendorFormAddressField(address: string) {
    this.parent.patchValue({
      vendor: {
        vendorAddress: address,
      },
    });
  }

  patchValuesInVendorForm(vendorSelectedRow: GetAllVendorsDto) {
    this.parent.patchValue({
      vendor: {
        vendorName: vendorSelectedRow.vendorName,
        vendorPhone: vendorSelectedRow.phoneNumber,
        vendorEmail: vendorSelectedRow.emailAddress,
        vendorFax: vendorSelectedRow.fax,
      },
    });
  }

  patchAccountNumber(acountNumber: string) {
    this.vendorAccountNumber = acountNumber;
    this.parent.patchValue({
      vendor: {
        vendorDirectDelivery: acountNumber,
        duplicateVendorDirectDelivery: acountNumber,
      },
    });
  }

  logValidationErrors() {
    this.validationErrorEvent.emit();
  }

  patchPoAssignment(vendorNumber: string) {
    let lineStatus = false;
    this.quoteOrderLine.forEach(x => {
      if (x.salesOrderLine?.vendorDirectDeliveryD365 === vendorNumber) {
        lineStatus = true;
      }
    });
    if (lineStatus) {
      this.poAssignment = "Default";
    } else {
      this.poAssignment = "New";
    }
  }

  redirectToVendorSummary() {
    // this._router.navigate([
    //   `vendors/vendor-summary/${this.vendorAccountNumber}`
    // ]);
    const vendorAccount = this.parent.get([
      "vendor",
      "vendorDirectDelivery",
    ])?.value;
    if (vendorAccount) {
      this.vendorDisplayModal = true;
    }
  }

  closeModal() {
    this.vendorDisplayModal = false;
  }
}
